import React, { useEffect, useState } from 'react'
import { Box, Fab, Tooltip } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline'
import { type Row } from 'react-table'
import { AccessLevel, useSessionContext } from 'session'
import {
  ExecutiveOrgFilters,
  type ExecutiveOrgSearchFilters,
  IconButtonWithTooltip,
  initialExecutiveOrgSearchFilters,
  type TableEntity,
  UsTable,
  useGlobalMessageHandler
} from 'components'
import { executivePositionsSearchPath } from 'helpers'
import {
  type ExecOrgSearchItem,
  type ExecutiveOrgSearchParams,
  type ExecutiveOrgSearchResults,
  searchExecutiveOrganizations
} from 'services'

const PAGE_SIZE = 50

const additionalColumns = [
  { Header: 'Sort Order', accessor: 'sortOrder', order_field: 'sort_order' },
  { Header: 'Name', accessor: 'name', order_field: 'name' },
  { Header: 'Code', accessor: 'code', order_field: 'code' },
  { Header: 'Role', accessor: 'role.desc', order_field: 'role' },
  { Header: 'State', accessor: 'state.abbrev', order_field: 'state' },
  { Header: 'Parent', accessor: 'parentOrg.name', order_field: 'parent' }
]

export const ExecutiveOrgSearchPage: React.FC = () => {
  const { changeModuleName, changeUniqueId, openNewTab, hasAccessLevel } = useSessionContext()
  const { showErrorMessage } = useGlobalMessageHandler()

  useEffect(() => {
    changeModuleName('Executive Organizations')
    changeUniqueId('')
  }, [changeModuleName, changeUniqueId])

  const [orgs, setOrgs] = useState<TableEntity[]>([])
  const [filters, setFilters] = useState<ExecutiveOrgSearchFilters>(initialExecutiveOrgSearchFilters)
  const [lastQueriedFilters, setLastQueriedFilters] = useState<ExecutiveOrgSearchFilters>(initialExecutiveOrgSearchFilters)
  const [loadResultWithFilters, setLoadResultWithFilters] = useState(false)
  const [pageCount, setPageCount] = useState<number>(0)
  const [totalCount, setTotalCount] = useState<number | null>(null)
  const [loading, setLoading] = useState<boolean>(false)

  const updateFilters = (updates: Partial<ExecutiveOrgSearchFilters>): void => {
    setFilters(prevState => ({ ...prevState, ...updates }))
  }

  const cleanFilter = (): void => {
    const isPristine = JSON.stringify(initialExecutiveOrgSearchFilters) === JSON.stringify(lastQueriedFilters)
    setFilters(initialExecutiveOrgSearchFilters)
    if (!isPristine) {
      setLoadResultWithFilters(prevState => !prevState)
    }
  }

  const fetchOrgs = async (): Promise<ExecutiveOrgSearchResults> => {
    setLoading(true)
    const params: ExecutiveOrgSearchParams = {
      name: filters.name.trim() !== '' ? filters.name.trim() : undefined,
      code: filters.code.trim() !== '' ? filters.code.trim() : undefined,
      role_id: filters.role != null ? filters.role.id : undefined,
      state_id: filters.state != null ? filters.state.id : undefined,
      page: filters.page !== 0 ? filters.page : undefined
    }
    params.order_by = filters.order_field
    params.order_direction = filters.order_direction
    params.page_size = PAGE_SIZE
    return await searchExecutiveOrganizations(params)
  }

  const updateTableWithFilter = async (): Promise<void> => {
    fetchOrgs().then(response => {
      setOrgs(response.data)
      setPageCount(response.total_pages)
      setTotalCount(response.total_items)
      setLastQueriedFilters(filters)
      if (filters.page !== response.page_number) {
        updateFilters({ page: response.page_number })
      }
    }).catch(() => {
      setOrgs([])
      setPageCount(0)
      setTotalCount(0)
      showErrorMessage('Failed to load executive organizations. Please try again.')
    }).finally(() => { setLoading(false) })
  }

  useEffect(() => {
    void updateTableWithFilter()
  }, [loadResultWithFilters])

  const triggerReload = (): void => {
    setLoadResultWithFilters(prevState => !prevState)
  }

  const openEditOrg = (entity: TableEntity): void => {
    entity.id != null
      ? openNewTab(`/us/executives/orgs/${entity.id}`)
      : showErrorMessage('No ID on this organization.')
  }

  const columns = [
    ...additionalColumns,
    {
      Header: 'Positions',
      accessor: 'topId',
      order_field: '',
      Cell: ({ row }: { row: Row<ExecOrgSearchItem> }) => (
        <IconButtonWithTooltip
          label='View Positions'
          color='primary'
          onClick={() => { openNewTab(executivePositionsSearchPath(row.original.id, row.original.topId)) }}
          icon={<PeopleOutlineIcon />}
        />
      )
    }
  ]

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <Box width="97%" sx={{ pb: 1 }}>
        {hasAccessLevel(AccessLevel.MANAGER) && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Tooltip title="Create organization">
              <span>
                <Fab color="primary" aria-label="create organization" onClick={() => { openNewTab('/us/executives/orgs/create') }}>
                  <AddIcon />
                </Fab>
              </span>
            </Tooltip>
          </Box>
        )}
        <ExecutiveOrgFilters
          filters={filters}
          updateFilters={updateFilters}
          doFilter={updateTableWithFilter}
          cleanFilters={cleanFilter}
        />
        <UsTable
          entities={orgs}
          filters={filters}
          updateFilters={updateFilters}
          triggerReload={triggerReload}
          pageCount={pageCount}
          totalCount={totalCount}
          additionalColumns={columns}
          orderField={filters.order_field}
          orderDirection={filters.order_direction}
          loading={loading}
          openEdit={openEditOrg}
        />
      </Box>
    </Box>
  )
}
